import { Fragment, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ChevronDown, LogOut, Loader2, Mail, Phone } from "lucide-react";
import { useAuth, authedGet } from "@/context/AuthContext";
import { formatGBP } from "@/config/pricing";
import Atmosphere from "@/components/silver/layout/Atmosphere";

const fmt = (n) => Number(n || 0).toLocaleString("en-GB");
const date = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function Detail({ e }) {
  const f = e.filters;
  const items = [
    ["Audience", e.audiencePreset],
    ["Stage", f.retirement.join(", ") || "Any"],
    ["Bedrooms", f.property.join(", ") || "Any"],
    ["Signals", ["downsize", "capitalRelease", "sheltered", "care"].filter((k) => f[k]).join(f.opportunityMatch === "all" ? " AND " : " OR ") || "Any"],
    ["Format", e.format || "—"],
    ["Creative", e.creativeTheme || "—"],
    ["Angle", e.campaignAngle || "—"],
    ["Services", e.services.join(", ") || "—"],
    ["Free sample pack", e.customer.freeSample ? "Yes — post samples" : "No"],
    ["Campaign message", e.campaignMessage || "—"],
    ["Customer message", e.customer.message || "—"],
    ["Email notification", e.emailStatus],
  ];
  return (
    <div className="grid gap-x-8 gap-y-3 p-6 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k}><p className="eyebrow-muted">{k}</p><p className="mt-1 whitespace-pre-wrap text-[14px] text-white/85">{v}</p></div>
      ))}
    </div>
  );
}

export default function AdminEnquiries() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!user) return;
    authedGet("/admin/enquiries").then(setRows).catch(() => setError("Could not load briefs. Please sign in again."));
  }, [user]);

  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#C6EE5A]" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const totals = (rows || []).reduce((a, r) => ({ reach: a.reach + r.selectedQuantity, value: a.value + r.estimatedPrice }), { reach: 0, value: 0 });

  return (
    <div className="min-h-screen px-5 py-8 sm:px-8">
      <Atmosphere />
      <div className="mx-auto max-w-7xl">
        <header className="glass flex items-center justify-between !rounded-full px-5 py-3">
          <Link to="/" className="flex items-center gap-3"><img src="/brand/nichecom-wordmark-dark.png" alt="NicheCom" className="h-5 w-auto" /><span className="hidden text-[13px] font-semibold sm:inline">Campaign briefs</span></Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-[13px] text-white/55 sm:inline">{user.email}</span>
            <button type="button" className="btn-ghost !py-2 text-[13px]" onClick={logout} data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </header>

        <div className="mt-12">
          <p className="eyebrow">Team dashboard</p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-[-0.045em] sm:text-5xl">Campaign briefs</h1>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {[["Briefs received", fmt(rows?.length)], ["Households requested", fmt(totals.reach)], ["Estimated base value", formatGBP(Math.round(totals.value * 100) / 100)]].map(([k, v]) => (
            <div key={k} className="glass p-6"><p className="eyebrow-muted">{k}</p><p className="num mt-2 text-3xl font-extrabold tracking-[-0.04em]" data-testid={`admin-stat-${k.split(" ")[0].toLowerCase()}`}>{v}</p></div>
          ))}
        </div>

        {error && <p className="mt-6 text-[#FCA5A5]">{error}</p>}
        {!rows && !error && <div className="mt-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#C6EE5A]" /></div>}
        {rows && rows.length === 0 && <div className="glass mt-8 p-10 text-center text-white/60" data-testid="admin-empty">No campaign briefs yet.</div>}
        {rows && rows.length > 0 && (
          <div className="glass mt-8 overflow-x-auto p-2" data-testid="admin-enquiries-table">
            <table className="w-full min-w-[880px] text-left text-[14px]">
              <thead>
                <tr className="font-ui text-[10.5px] uppercase tracking-[0.12em] text-white/45">
                  {["Reference", "Received", "Agency", "Contact", "Districts", "Reach", "Est. base", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <Fragment key={e.id}>
                    <tr className="cursor-pointer border-t border-white/[0.07] transition-colors hover:bg-white/[0.03]" onClick={() => setOpen(open === e.id ? null : e.id)} data-testid={`admin-row-${e.reference}`}>
                      <td className="px-4 py-4 font-ui text-[12px] font-semibold tracking-[0.06em] text-[#C6EE5A]">{e.reference}</td>
                      <td className="px-4 py-4 text-white/60">{date(e.createdAt)}</td>
                      <td className="px-4 py-4"><p className="font-semibold">{e.customer.agency}</p><p className="text-[12px] text-white/45">{e.customer.branch || ""}</p></td>
                      <td className="px-4 py-4">
                        <p>{e.customer.firstName} {e.customer.lastName}</p>
                        <p className="flex gap-3 text-[12px] text-white/50"><a href={`mailto:${e.customer.email}`} onClick={(ev) => ev.stopPropagation()} className="inline-flex items-center gap-1 hover:text-white"><Mail className="h-3 w-3" />{e.customer.email}</a><span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{e.customer.telephone}</span></p>
                      </td>
                      <td className="max-w-[160px] truncate px-4 py-4 font-ui text-[12px]">{e.postcodeDistricts.join(", ")}</td>
                      <td className="num px-4 py-4">{fmt(e.selectedQuantity)}<span className="block text-[11px] text-white/40">of {fmt(e.matchingHouseholds)}</span></td>
                      <td className="num px-4 py-4 font-semibold">{formatGBP(e.estimatedPrice)}</td>
                      <td className="px-4 py-4"><ChevronDown className={`h-4 w-4 transition-transform ${open === e.id ? "rotate-180" : ""}`} /></td>
                    </tr>
                    {open === e.id && <tr><td colSpan={8} className="border-t border-white/[0.05] bg-black/20"><Detail e={e} /></td></tr>}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
